import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { MeResponseSchema, type AccessError, type MeResponse } from "@hyphesync/shared";
import { useApi } from "./api";
import { classifyFailure, shouldAcceptInvite, spendsInvite } from "./rules";
import { clearInvite, pendingInvite } from "./invite";

/** Why a signed-in person can't use the app: exactly the API's access codes, one screen each. */
export type Blocked = AccessError;

type State =
  | { kind: "loading" }
  | { kind: "ready"; me: MeResponse }
  | { kind: "blocked"; reason: Blocked }
  | { kind: "expired" }
  | { kind: "error" };

const SessionContext = createContext<MeResponse | null>(null);

/** The resolved caller. Only rendered inside <SessionGate>'s ready branch. */
export function useMe(): MeResponse {
  const me = useContext(SessionContext);
  if (!me) throw new Error("useMe outside a ready session");
  return me;
}

/** Loads /api/me. If the caller has no users row and an invite token is waiting, accepts it
 *  first. Every failure is caught and rendered; a mount load never leaves an unhandled
 *  rejection (SunshineBids §4). */
export function SessionGate(props: {
  children: ReactNode;
  renderBlocked: (reason: Blocked, retry: () => void) => ReactNode;
  renderLoading: () => ReactNode;
  renderExpired: () => ReactNode;
  renderError: (retry: () => void) => ReactNode;
}) {
  const api = useApi();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    const settle = (s: State) => { if (alive) setState(s); };
    setState({ kind: "loading" });
    (async () => {
      try {
        try {
          settle({ kind: "ready", me: MeResponseSchema.parse(await api("GET", "/api/me")) });
          clearInvite(); // a member with a stale link: nothing to accept
          return;
        } catch (e) {
          const token = pendingInvite();
          if (!shouldAcceptInvite(e, token)) throw e;
          const accepted = MeResponseSchema.parse(await api("POST", "/api/invitations/accept", { token }));
          clearInvite();
          settle({ kind: "ready", me: accepted });
        }
      } catch (e) {
        const f = classifyFailure(e);
        if (f.kind === "blocked" && spendsInvite(f.reason)) clearInvite();
        settle(f);
      }
    })();
    return () => { alive = false; };
  }, [api, attempt]);

  if (state.kind === "loading") return <>{props.renderLoading()}</>;
  if (state.kind === "expired") return <>{props.renderExpired()}</>;
  if (state.kind === "error") return <>{props.renderError(retry)}</>;
  if (state.kind === "blocked") return <>{props.renderBlocked(state.reason, retry)}</>;
  return <SessionContext.Provider value={state.me}>{props.children}</SessionContext.Provider>;
}
